// InputController -- the DOM event adapter for mouse and keyboard.
//
//   new InputController({ canvas, world, renderer, hud, minimap, player })
//     .update(dt)          per display frame: prune, ghost, camera, cursor
//     .selection           Set<entityId>, iterated in a STABLE order
//     .controlGroups       Array(10) of id arrays
//
// This file deliberately contains no decisions. Every "which unit did they mean,
// what does that click mean, what does that key mean" question is answered by
// pure functions in ./selection.js; what lives here is listener plumbing,
// projection to screen space, and posting commands.
//
// Three rules the whole feel hangs off:
//
//  1. SELECTION UPDATES ON MOUSEDOWN. Waiting for mouseup adds one whole click's
//     worth of lag to the single most frequent action in the game, and it is
//     the difference between "instant" and "mushy". A press that later turns
//     into a drag simply re-resolves as a box while the button is held, so the
//     early commit costs nothing.
//
//  2. COMMANDS GO THROUGH world.issueNow(). world.issue() queues to the start of
//     the next 24 Hz tick -- up to 42 ms of latency on a 144 Hz display, for no
//     benefit, since local input is not part of the deterministic replay path.
//
//  3. THE CAMERA ONLY EVER SEES ARROW KEYS. It happily pans on WASD too, but A
//     is attack-move and S is stop; handing it the raw key set would make every
//     attack-move jerk the camera left. It gets a filtered set.
//
//  4. THIS MODULE OWNS THE KEYBOARD OUTRIGHT. Not "mostly", not "except for the
//     four kinds of key the HUD claims" -- outright. Every press goes to
//     resolveKey() in ./selection.js, which answers from ONE table (the command
//     card, rebuilt fresh so it can never be stale) under one declared
//     precedence order: reserved > placement > menu > card > global > camera.
//     src/ui/hud.js enforces its half by making `ownsHotkeys` a getter that
//     reports false while an input controller is attached, so the split that
//     left B-then-S unreachable cannot come back by convention drift.

import {
  SELECTION_LIMIT, PLAYER, MAP_W, MAP_H,
} from '../sim/constants.js';
import { UnitDefs, BuildingDefs, ResearchDefs } from '../sim/defs.js';
import { edgeDist } from '../sim/world.js';
import { techMet, unavailableReason, validatePlacement } from '../sim/commands.js';
import { canSee } from '../sim/fog.js';
import {
  CLICK_SLOP_PX, DOUBLE_CLICK_MS, GROUP_TAP_MS,
  MIN_UNIT_PICK_PX, MIN_BUILDING_PICK_PX,
  normRect, isDragGesture, hitTest, boxSelectAll, sameTypeOnScreen,
  addIds, toggleIds, pruneIds, centroid, resolveRightClick,
  resolveKey, toInputAction, applyGroup, isBuilder, resolveTargetedClick,
  cardFor, cardPageFor,
} from './selection.js';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Top of each model in world units -- mirrors render/units.js MODEL_TOP. */
const MODEL_TOP = {
  worker: 0.78, rifleman: 1.08, bruiser: 0.92, siege_tank: 0.76, wraith: 0.40,
};
const FLY_ALT = 2.3;
const EDGE_PX = 8;          // pointer edge-scroll band, matches render/camera.js
const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/** A unit whose only order is the never-ending gather loop. See issue(). */
const mining = (e) => !!(e && e.kind === 'unit' && e.order && e.order.type === 'gather'
  && (!e.orderQueue || e.orderQueue.length === 0));

/** How tall an entity's pick column is, in world units above the terrain. */
function modelTop(e) {
  if (e.kind === 'unit') return (MODEL_TOP[e.defId] === undefined ? 0.9 : MODEL_TOP[e.defId]) + (e.flying ? FLY_ALT : 0);
  if (e.kind === 'building') {
    const d = BuildingDefs[e.defId];
    return d ? Math.max(1.0, d.size * 0.5) : 1.5;
  }
  return 0.55;
}

export class InputController {
  constructor({ canvas, world, renderer, hud = null, minimap = null, player = PLAYER } = {}) {
    this.canvas = canvas;
    this.world = world;
    this.renderer = renderer;
    this.hud = hud;
    this.minimap = minimap;
    this.player = player;

    /** @type {Set<number>} iterated in selectionOrder order. */
    this.selection = new Set();
    /** Stable order: additions append, removals splice. The HUD reads this. */
    this.selectionOrder = [];
    /** @type {number[][]} */
    this.controlGroups = Array.from({ length: 10 }, () => []);

    this.mode = 'normal';          // 'normal' | 'build' | 'placement'
    this.cursor = null;            // null | 'attackmove' | 'patrol' | 'move' | 'gather' | 'repair' | 'rally'
    this.placementDef = null;
    /** The command card's "next click is the target" request, if any. */
    this.pending = null;
    this.pendingAction = null;
    /** Tells the HUD to stand down its own keydown listener. See _onKeyDown. */
    this.ownsHotkeys = true;
    /** Last key this layer resolved: { key, layer, action }. Read by tests. */
    this.lastKey = null;
    /** Units a gesture left out because of SELECTION_LIMIT, and the total. */
    this.selectionCapped = 0;
    this.selectionTotal = 0;
    /** While placing: { defId, x, y, ok, reason } -- the ghost, in words. */
    this.placementStatus = null;
    /**
     * The production the player asked for and did not get, and why:
     * { defId, label, reason }. Maintained by _updateProduction() below.
     */
    this.productionStatus = null;

    this.keys = new Set();
    this.mouse = { x: 0, y: 0, cx: 0, cy: 0, inside: false };
    this.drag = null;              // { x0,y0,x1,y1, moved, shift, base }
    this.minimapDrag = false;

    this._picks = [];
    this._camKeys = new Set();
    this._lastClick = { t: -1e9, id: 0, x: 0, y: 0 };
    this._lastGroup = { slot: -1, t: -1e9 };
    this._selDirty = true;
    this._ghostOn = false;
    this._boxEl = null;
    this._bound = [];

    // ONE MODULE OWNS THE KEYBOARD, AND IT IS THIS ONE.
    //
    // Not by agreement -- by construction. This layer resolves every key
    // against the same command card the HUD draws (see resolveKey in
    // ./selection.js), so there is nothing on screen it cannot reach and no
    // reason for a second listener to exist. hud.ownsHotkeys is switched off
    // here, AND the HUD refuses to own the keyboard whenever an input
    // controller is attached, so neither side can take it back by accident.
    if (this.hud && 'ownsHotkeys' in this.hud) this.hud.ownsHotkeys = false;

    this._bind();
    this._syncSelection();
  }

  // ---- listener plumbing ---------------------------------------------------
  _on(target, type, fn, opts) {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(type, fn, opts);
    this._bound.push([target, type, fn]);
  }

  _bind() {
    const win = typeof window !== 'undefined' ? window : null;
    const cv = this.canvas;

    this._on(cv, 'mousedown', (e) => this._onCanvasDown(e));
    this._on(cv, 'contextmenu', (e) => { if (e.preventDefault) e.preventDefault(); });
    this._on(cv, 'mouseenter', () => { this.mouse.inside = true; });
    this._on(cv, 'mouseleave', () => { this.mouse.inside = false; });

    // Move/up live on the window so a drag that leaves the canvas still tracks
    // and still finishes -- releasing the button over the HUD must not leave a
    // selection box stuck on screen.
    this._on(win, 'mousemove', (e) => this._onMove(e));
    this._on(win, 'mouseup', (e) => this._onUp(e));
    this._on(win, 'blur', () => this._onBlur());
    this._on(win, 'keydown', (e) => this._onKeyDown(e));
    this._on(win, 'keyup', (e) => this._onKeyUp(e));

    const mm = this.minimap && (this.minimap.canvas || (this.hud && this.hud.minimapCanvas));
    this.minimapCanvas = mm || null;
    if (mm) {
      this._on(mm, 'mousedown', (e) => this._onMinimapDown(e));
      this._on(mm, 'contextmenu', (e) => { if (e.preventDefault) e.preventDefault(); });
    }
  }

  dispose() {
    for (const [t, type, fn] of this._bound) {
      if (t && typeof t.removeEventListener === 'function') t.removeEventListener(type, fn);
    }
    this._bound.length = 0;
    if (this._boxEl && this._boxEl.remove) this._boxEl.remove();
    this._boxEl = null;
  }

  // ---- coordinates ---------------------------------------------------------
  _rectOf(el) {
    if (el && typeof el.getBoundingClientRect === 'function') {
      const r = el.getBoundingClientRect();
      if (r) return r;
    }
    return { left: 0, top: 0, width: 1, height: 1 };
  }

  _local(e, el) {
    const r = this._rectOf(el || this.canvas);
    return { x: (e.clientX || 0) - (r.left || 0), y: (e.clientY || 0) - (r.top || 0) };
  }

  /** The canvas rect in CSS pixels, falling back to the camera's viewport. */
  _canvasSize() {
    const r = this._rectOf(this.canvas);
    const vp = this._viewport();
    // _rectOf's own fallback is a 1x1 rect, which as a bounds test would reject
    // every pixel on the screen. Anything that degenerate defers to the camera.
    return { w: r.width > 1 ? r.width : vp.w, h: r.height > 1 ? r.height : vp.h };
  }

  /**
   * True when a pointer pixel is actually ON the canvas.
   *
   * A world point can project WELL OUTSIDE the canvas without being "behind"
   * the camera -- the enemy base projects to x=1668 on a 1600 px canvas -- so
   * anything that projects a world position and then feeds the pixel back in
   * as a click can hand this layer a coordinate no mouse could ever produce.
   * screenToWorld() answers such a pixel anyway (it clamps to the map rect, and
   * off to the side of the viewport it does not even need to clamp), which
   * turns "order the army across the map" into "order the army to a spot near
   * the edge of the screen" -- exactly where a blind evaluator watched eight
   * riflemen walk to and die.
   *
   * There is no legitimate press to lose here: mousedown is bound to the canvas,
   * so a real pointer outside it never reaches this listener at all.
   */
  _onCanvas(sx, sy) {
    const { w, h } = this._canvasSize();
    return sx >= 0 && sy >= 0 && sx <= w && sy <= h;
  }

  /** Ground point under a canvas pixel, in tiles. */
  worldAt(sx, sy) {
    const p = this.renderer.screenToWorld(sx, sy);
    return p || { x: 0, y: 0 };
  }

  _viewport() {
    const cam = this.renderer && this.renderer.camera;
    const w = (cam && cam.vw) || this.canvas.clientWidth || this.canvas.width || 1;
    const h = (cam && cam.vh) || this.canvas.clientHeight || this.canvas.height || 1;
    return { w, h };
  }

  _groundAt(x, y) {
    const f = this.renderer && this.renderer.sampleHeight;
    return typeof f === 'function' ? f(x, y) : 0;
  }

  /** Screen pixels per world tile at the camera focus (zoom-dependent). */
  _pixelScale() {
    const cam = this.renderer.camera;
    const f = (cam && cam.focus) || { x: 48, y: 48 };
    const a = this.renderer.worldToScreen(f.x, f.y, 0);
    const b = this.renderer.worldToScreen(f.x + 1, f.y, 0);
    const c = this.renderer.worldToScreen(f.x, f.y + 1, 0);
    const px = Math.abs(b.x - a.x) || 24;
    const py = Math.abs(c.y - a.y) || 20;
    return { px, py };
  }

  /**
   * Project every entity the player is allowed to interact with into a screen
   * "pick" -- the vertical column from the z=0 plane to the top of its model,
   * plus a pixel radius. Rebuilt per gesture (mousedown, and each frame of a
   * live box drag), never per render frame.
   */
  buildPicks() {
    const out = this._picks;
    out.length = 0;
    const w = this.world, me = this.player;
    const { w: vw, h: vh } = this._viewport();
    const { px, py } = this._pixelScale();
    const MARGIN = 120;

    for (const e of w.entities.values()) {
      if (!e || !e.alive) continue;
      if (e.owner >= 0 && e.owner !== me && !canSee(w, me, e)) continue;   // fogged

      const ground = this._groundAt(e.x, e.y);
      const base = this.renderer.worldToScreen(e.x, e.y, 0);
      const top = this.renderer.worldToScreen(e.x, e.y, ground + modelTop(e));
      if (base.behind && top.behind) continue;

      const minX = Math.min(base.x, top.x), maxX = Math.max(base.x, top.x);
      const minY = Math.min(base.y, top.y), maxY = Math.max(base.y, top.y);
      if (maxX < -MARGIN || minX > vw + MARGIN || maxY < -MARGIN || minY > vh + MARGIN) continue;

      // TWO radii, and the difference between them is load-bearing.
      //
      //   rx/ry           the pick radius: how close counts as a hit. It has a
      //                   PIXEL FLOOR so a worker stays clickable zoomed out.
      //   bodyRx/bodyRy   how big the thing actually draws, floor and all
      //                   forgiveness removed.
      //
      // The floor is forgiveness for empty space. Zoomed out it is nearly twice
      // a worker's drawn width, and that surplus reaches sideways into whatever
      // the worker happens to be standing next to -- which is how a click aimed
      // squarely at a Barracks comes back holding the worker walking past it.
      // hitTest() uses the body radius to tell "on the unit" from "merely near
      // it" whenever a building is genuinely under the cursor too.
      let rx, ry, bodyRx, bodyRy;
      if (e.kind === 'unit') {
        const r = (e.radius || 0.35) + 0.22;
        bodyRx = r * px; bodyRy = r * py;
        rx = Math.max(MIN_UNIT_PICK_PX, bodyRx);
        ry = Math.max(MIN_UNIT_PICK_PX, bodyRy);
      } else {
        const hw = ((e.w || 2) / 2) + 0.15, hh = ((e.h || 2) / 2) + 0.15;
        const floor = e.kind === 'building' ? MIN_BUILDING_PICK_PX : 8;
        bodyRx = hw * px; bodyRy = hh * py;
        rx = Math.max(floor, bodyRx);
        ry = Math.max(floor, bodyRy);
      }

      out.push({
        e, bx: base.x, by: base.y, tx: top.x, ty: top.y, rx, ry, bodyRx, bodyRy,
        onScreen: maxX >= 0 && minX <= vw && maxY >= 0 && minY <= vh,
      });
    }
    return out;
  }

  /** The entity under a canvas pixel, or null. Resources included. */
  pickAt(sx, sy) {
    return hitTest(this.buildPicks(), sx, sy, this.player);
  }

  // ---- selection state -----------------------------------------------------
  _syncSelection() {
    this.selection.clear();
    for (const id of this.selectionOrder) this.selection.add(id);
    this._selDirty = true;
  }

  setSelection(ids) {
    this.selectionOrder = (ids || []).slice(0, SELECTION_LIMIT);
    this._syncSelection();
    // Any selection that did not come from a capped gesture clears the notice.
    // _commit() re-arms it immediately afterwards when it did.
    this.selectionCapped = 0;
    this.selectionTotal = this.selectionOrder.length;
    return this;
  }

  /**
   * Select `ids`, remembering how many units the gesture actually touched.
   *
   * SELECTION_LIMIT is 24 and a late-game box drag routinely covers 70. Handing
   * back 24 with no comment is how "select army, attack" quietly leaves two
   * thirds of the army standing at home, so the shortfall is recorded here and
   * surfaced twice: a flash when the gesture ends, and a standing badge on the
   * selection panel for as long as the capped selection is the live one.
   */
  _commit(ids, total) {
    this.setSelection(ids);
    this.selectionTotal = Math.max(total || 0, this.selectionOrder.length);
    this.selectionCapped = Math.max(0, this.selectionTotal - this.selectionOrder.length);
    return this.selectionCapped;
  }

  /** Say so, once, when a gesture just hit the cap. */
  _flashCap() {
    if (this.selectionCapped <= 0) return;
    this._flash(`Selection limit ${SELECTION_LIMIT}: ${this.selectionCapped} more unit${this.selectionCapped === 1 ? '' : 's'} not selected`);
  }
  selectIds(ids) { return this.setSelection(ids); }
  clearSelection() { return this.setSelection([]); }

  /** Live entities in stable selection order. */
  selectedEntities() {
    const out = [];
    for (const id of this.selectionOrder) {
      const e = this.world.get(id);
      if (e && e.alive) out.push(e);
    }
    return out;
  }
  get selected() { return this.selectedEntities(); }

  /** The worker a build order goes to: a card button may name its own. */
  _selectedBuilder() {
    const pool = this.placementIds
      ? this.placementIds.map((id) => this.world.get(id)).filter((e) => e && e.alive)
      : this.selectedEntities();
    for (const e of pool) if (isBuilder(e) && e.owner === this.player) return e;
    return null;
  }

  // ---- command posting -----------------------------------------------------
  /**
   * Post a command with zero latency.
   *
   * One wrinkle: a gather order never finishes -- a worker mines until it is
   * told to do something else. Shift-queueing anything behind a mining worker
   * would therefore park the command forever, and shift-queued build chains
   * (the whole reason shift exists on the build hotkeys) would silently do
   * nothing. So a queued command aimed at a unit whose ONLY order is gather
   * replaces it instead; every later link in the chain queues normally behind
   * the build it is following.
   */
  issue(cmd) {
    if (!cmd) return;
    const w = this.world;
    const ids = Array.isArray(cmd.ids) ? cmd.ids : [];
    if (!cmd.queued || !ids.length) { w.issueNow({ player: this.player, ...cmd }); return; }

    if (cmd.type === 'build') {
      // never split a build: applyCommand pays per command, so two of them
      // would charge the player twice for one structure.
      const e = w.get(ids[0]);
      w.issueNow({ player: this.player, ...cmd, queued: !mining(e) });
      return;
    }

    const nowIds = [], laterIds = [];
    for (const id of ids) {
      const e = w.get(id);
      if (!e || !e.alive) continue;
      (mining(e) ? nowIds : laterIds).push(id);
    }
    if (nowIds.length) w.issueNow({ player: this.player, ...cmd, ids: nowIds, queued: false });
    if (laterIds.length) w.issueNow({ player: this.player, ...cmd, ids: laterIds, queued: true });
  }
  _issueAll(cmds) { for (const c of cmds) this.issue(c); }

  _flash(msg) {
    if (msg && this.hud && typeof this.hud.flash === 'function') this.hud.flash(msg);
  }

  /**
   * A production key that could not be obeyed, said out loud AND written down.
   *
   * The flash is 2.6 seconds long and lives in the middle of the screen; a
   * player macroing off the command card is looking at the bottom right and has
   * usually pressed the key twice more by the time it fades. That is the report
   * we got: "no feedback when a queued production keypress is rejected for funds
   * while the building panel is open" -- there WAS feedback, it was just gone.
   *
   * So a refused press does what an armed building ghost already does (see
   * placementStatus): it leaves a standing line, carrying the exact sentence the
   * simulation refused with, which stays up for as long as the refusal is still
   * true and clears itself the moment the money lands. Reads the same to a
   * player glancing down and to a test.
   */
  _refuseProduction(defId, reason, label = null) {
    this._flash(reason);
    if (!defId || !reason) return false;
    const def = UnitDefs[defId] || ResearchDefs[defId] || null;
    this.productionStatus = { defId, label: label || (def && def.name) || defId, reason };
    return false;
  }

  /**
   * Keep -- or drop -- the standing production refusal.
   *
   * The command card is the one table this layer answers keys from, so it is
   * also the only honest source for whether the refusal still stands. If the
   * card no longer offers that item (the player selected something else, the
   * structure died) the notice is not about anything any more; if the button has
   * gone live, the player can simply press the key again and does not need to be
   * told they are poor.
   */
  _updateProduction() {
    const st = this.productionStatus;
    if (!st) return;
    const card = this.currentCard();
    let btn = null;
    for (const s of card.slots) {
      if (!s || !s.action || s.action.defId !== st.defId) continue;
      if (s.action.type !== 'train' && s.action.type !== 'research') continue;
      btn = s; break;
    }
    if (!btn || btn.enabled) { this.productionStatus = null; return; }
    st.label = btn.label || st.label;
    st.reason = btn.reason || st.reason;
  }

  // ---- mouse ---------------------------------------------------------------
  _onCanvasDown(e) {
    if (e.button === 1) return;                     // middle-drag belongs to the camera
    const { x: sx, y: sy } = this._local(e, this.canvas);
    this.mouse.x = sx; this.mouse.y = sy;
    this.mouse.cx = e.clientX || 0; this.mouse.cy = e.clientY || 0;
    this.mouse.inside = true;
    const shift = !!e.shiftKey, ctrl = !!(e.ctrlKey || e.metaKey);

    if (e.preventDefault) e.preventDefault();

    // A pixel that is not on the canvas is not a place on the battlefield, for
    // selection or for orders. See _onCanvas: screenToWorld() will happily
    // answer such a pixel with a real-looking tile, and acting on that answer
    // sends units somewhere the player never pointed.
    if (!this._onCanvas(sx, sy)) {
      if (this._isOrderClick(e.button)) this._flashOffScreen();
      return;
    }

    // A press on the command bar belongs to the chrome, not the battlefield.
    // An ORDER aimed there, though, is a player pointing at a place the camera
    // is not showing -- which used to vanish without a word, and reads as the
    // game having dropped the order. Say where such an order belongs instead.
    if (this.hud && typeof this.hud.isOverHud === 'function'
        && this.hud.isOverHud(this.mouse.cx, this.mouse.cy)) {
      if (this._isOrderClick(e.button)) this._flashOffScreen();
      return;
    }

    if (e.button === 2) { this._rightClick(sx, sy, shift); return; }
    if (e.button !== 0) return;

    if (this.mode === 'placement') { this._confirmPlacement(sx, sy, shift); return; }
    if (this.pending || this.cursor) { this._cursorClick(sx, sy, shift); return; }

    this._selectClick(sx, sy, shift, ctrl);
  }

  /** True when this press was meant as an order rather than as a selection. */
  _isOrderClick(button) {
    if (button === 2) return this.selectionOrder.length > 0;
    if (button !== 0) return false;
    return this.mode === 'placement' || !!this.cursor || !!this.pending;
  }

  /**
   * The click did not land on the map. Point at the thing that does work.
   * A placement ghost needs the camera moved; an order does not -- the minimap
   * takes orders anywhere on the map, and most players never discover that.
   */
  _flashOffScreen() {
    this._flash(this.mode === 'placement'
      ? 'Off screen: scroll the camera to place it there'
      : 'Off screen: right-click the minimap to order there');
  }

  /**
   * True when a click does not land on the map at all.
   *
   * screenToWorld() CLAMPS its answer to the map rectangle rather than failing,
   * so a click on the void past the rim -- or one whose pixel is not on screen
   * in the first place -- comes back as an edge tile that has nothing to do
   * with where the player pointed. Landing EXACTLY on the boundary is the
   * signature of that clamp: real ground answers with fractions, and the
   * outermost tiles are rim wall no one is ordering units onto anyway.
   */
  _offMap(sx, sy, w) {
    if (!w || !Number.isFinite(w.x) || !Number.isFinite(w.y)) return true;
    if (w.x <= 0 || w.y <= 0 || w.x >= MAP_W || w.y >= MAP_H) return true;
    const back = this.renderer && typeof this.renderer.worldToScreen === 'function'
      ? this.renderer.worldToScreen(w.x, w.y, 0) : null;
    return !!(back && back.behind);
  }

  _selectClick(sx, sy, shift, ctrl) {
    const picks = this.buildPicks();
    const raw = hitTest(picks, sx, sy, this.player);
    const hit = raw && raw.kind === 'resource' ? null : raw;
    const preOrder = this.selectionOrder.slice();

    const t = now();
    const mineUnit = hit && hit.kind === 'unit' && hit.owner === this.player;
    const dbl = mineUnit
      && this._lastClick.id === hit.id
      && t - this._lastClick.t <= DOUBLE_CLICK_MS
      && Math.hypot(sx - this._lastClick.x, sy - this._lastClick.y) <= 8;
    this._lastClick = { t, id: hit ? hit.id : 0, x: sx, y: sy };

    if (mineUnit && (dbl || ctrl)) {
      const all = sameTypeOnScreen(picks, hit.defId, this.player, Infinity);
      const want = shift ? addIds(preOrder, all, Infinity) : all;
      this._commit(want.slice(0, SELECTION_LIMIT), want.length);
      this._flashCap();
      this.drag = null;
      return;
    }

    if (hit) {
      this.setSelection(shift ? toggleIds(preOrder, [hit.id]) : [hit.id]);
    } else if (!shift) {
      this.setSelection([]);
    }

    this.drag = { x0: sx, y0: sy, x1: sx, y1: sy, moved: false, shift, base: shift ? preOrder : [] };
  }

  _onMove(e) {
    const { x: sx, y: sy } = this._local(e, this.canvas);
    this.mouse.x = sx; this.mouse.y = sy;
    this.mouse.cx = e.clientX || 0; this.mouse.cy = e.clientY || 0;
    const vp = this._viewport();
    this.mouse.inside = sx >= 0 && sy >= 0 && sx <= vp.w && sy <= vp.h;

    if (this.minimapDrag) { this._minimapTo(e); return; }

    const d = this.drag;
    if (!d) return;
    d.x1 = sx; d.y1 = sy;
    if (!d.moved && isDragGesture(d.x0, d.y0, sx, sy, CLICK_SLOP_PX)) d.moved = true;
    if (!d.moved) return;

    // Live box: the selection follows the rectangle while the button is held,
    // which is what makes a drag feel like it is doing something.
    this._applyBox(d);
  }

  /** Resolve the live box, counting what the cap left out. */
  _applyBox(d) {
    const rect = normRect(d.x0, d.y0, d.x1, d.y1);
    const all = boxSelectAll(this.buildPicks(), rect, this.player);
    const want = d.shift ? addIds(d.base, all, Infinity) : all;
    return this._commit(want.slice(0, SELECTION_LIMIT), want.length);
  }

  _onUp(e) {
    if (this.minimapDrag && (e.button === 0 || e.button === undefined)) { this.minimapDrag = false; return; }
    if (e.button !== 0) return;
    if (!this.drag) return;
    const { x: sx, y: sy } = this._local(e, this.canvas);
    const d = this.drag;
    d.x1 = sx; d.y1 = sy;
    if (!d.moved && isDragGesture(d.x0, d.y0, sx, sy, CLICK_SLOP_PX)) {
      // Released past the slop without an intermediate move event: still a box.
      d.moved = true;
      this._applyBox(d);
    }
    // The count changes on every frame of a live drag, so the notice waits for
    // the release -- one message per gesture, not one per mousemove.
    if (d.moved) this._flashCap();
    this.drag = null;
  }

  _onBlur() {
    this.keys.clear();
    this.drag = null;
    this.minimapDrag = false;
  }

  // ---- right click ---------------------------------------------------------
  _rightClick(sx, sy, shift) {
    // Right-click backs out of anything armed, INCLUDING the open build menu:
    // the menu is modal state like any other, and a player who opens it by
    // mistake should not have to hunt for Escape.
    if (this.mode !== 'normal' || this.cursor || this.pending) { this.cancelMode(); return; }
    const target = this.pickAt(sx, sy);
    const w = this.worldAt(sx, sy);
    if (!target && this._offMap(sx, sy, w)) {
      if (this.selectionOrder.length) this._flashOffScreen();
      return;
    }
    this._contextual(target, w.x, w.y, shift);
  }

  _contextual(target, x, y, queued) {
    const sel = this.selectedEntities();
    if (!sel.length) return;
    const cmds = resolveRightClick({ selection: sel, target, x, y, queued, player: this.player });
    this._issueAll(cmds);
  }

  _cursorClick(sx, sy, shift) {
    const pend = this.pending;
    const cursor = (pend && pend.type) || this.cursor;
    const target = this.pickAt(sx, sy);
    const w = this.worldAt(sx, sy);
    if (!target && this._offMap(sx, sy, w)) { this._flashOffScreen(); return; }
    // A card button names the units it applies to; a hotkey means "the selection".
    const sel = pend && pend.ids && pend.ids.length
      ? pend.ids.map((id) => this.world.get(id)).filter((e) => e && e.alive)
      : this.selectedEntities();
    const cmds = resolveTargetedClick(cursor, {
      selection: sel, target, x: w.x, y: w.y, queued: shift, player: this.player,
    });
    if (!cmds.length) {
      // e.g. Repair aimed at bare ground: say so and stay armed.
      this._flash(cursor === 'repair' ? 'Repair what?' : 'Invalid target');
      return;
    }
    this._issueAll(cmds);
    if (!shift) { this.cursor = null; this.pending = null; this.pendingAction = null; }
  }

  // ---- the command card's "next click is the target" path ------------------
  /**
   * Arm a targeted command coming from a HUD button. Mirrors what the matching
   * hotkey does, so a button and its key are one behaviour rather than two.
   */
  beginTargeting(req) {
    if (!req || !req.type) return false;
    const ids = req.ids && req.ids.length ? req.ids.slice() : this.selectionOrder.slice();
    if (!ids.length) return false;
    if (req.type === 'build') return this.beginPlacement(req.defId, ids);
    const type = req.type === 'attack' ? 'attackmove' : req.type;
    this.mode = 'normal';
    this.placementDef = null;
    this.cursor = type;
    this.pending = { type, ids, defId: req.defId || null };
    this.pendingAction = this.pending;
    return true;
  }
  startTargeting(req) { return this.beginTargeting(req); }
  setPending(req) { return this.beginTargeting(req); }
  beginCommand(req) { return this.beginTargeting(req); }

  // ---- placement -----------------------------------------------------------
  beginPlacement(defId, ids = null) {
    if (!BuildingDefs[defId]) return false;
    this.placementIds = ids && ids.length ? ids.slice() : null;
    if (!this._selectedBuilder()) { this._flash('Select a worker'); this.placementIds = null; return false; }
    if (!techMet(this.world, this.player, defId)) {
      this._flash(unavailableReason(this.world, this.player, defId) || 'Requires more tech');
      this.placementIds = null;
      return false;
    }
    this.mode = 'placement';
    this.placementDef = defId;
    this.cursor = null;
    this.pending = null;
    this.pendingAction = null;
    return true;
  }
  startPlacement(defId, ids) { return this.beginPlacement(defId, ids); }

  _confirmPlacement(sx, sy, shift) {
    const defId = this.placementDef;
    const worker = this._selectedBuilder();
    if (!defId || !worker) { this.cancelMode(); return; }
    const w = this.worldAt(sx, sy);
    const spot = validatePlacement(this.world, this.player, defId, w.x, w.y);
    if (!spot.ok) { this._flash(spot.reason); return; }
    const reason = unavailableReason(this.world, this.player, defId);
    if (reason) { this._flash(reason); return; }
    this.issue({ type: 'build', ids: [worker.id], defId, x: w.x, y: w.y, queued: !!shift });
    // Shift keeps the ghost up so a whole build chain can be laid out in one go.
    if (!shift) this.cancelMode();
  }

  cancelMode() {
    this.mode = 'normal';
    this.placementDef = null;
    this.placementIds = null;
    this.placementStatus = null;
    this.cursor = null;
    this.pending = null;
    this.pendingAction = null;
    if (this._ghostOn && this.renderer.setPlacement) { this.renderer.setPlacement(null); this._ghostOn = false; }
    // The command card follows `input.mode` (hud.cardMode), so clearing it here
    // is all the chrome needs -- and calling hud.setMode() from inside an action
    // the HUD itself dispatched would recurse straight back into runAction().
    return this;
  }
  cancel() { return this.cancelMode(); }

  // ---- keyboard ------------------------------------------------------------
  /**
   * The context resolveKey() decides against. Built fresh on every press, which
   * is what makes a stale command card -- the bug that made the build menu
   * keyboard-dead -- impossible rather than merely unlikely.
   */
  keyContext() {
    return {
      selection: this.selectedEntities(),
      mode: this.mode,
      player: this.player,
      world: this.world,
      armed: !!(this.cursor || this.pending),
    };
  }

  /** The command card that is live right now: the key table, and the picture. */
  currentCard() { return cardFor(this.keyContext()); }

  /** Which page of the card the HUD should be drawing. */
  get cardMode() { return cardPageFor(this.mode); }

  _onKeyDown(e) {
    const raw = e.key;
    if (raw === undefined || raw === null) return;
    const k = raw.length === 1 ? raw.toLowerCase() : raw;
    // A keydown for a key already held is auto-repeat. Hotkeys must not repeat.
    if (this.keys.has(k)) return;
    this.keys.add(k);
    if (k === 'Shift' || k === 'Control' || k === 'Alt' || k === 'Meta') return;

    // Layer 5, the camera: it reads this.keys itself, once per frame.
    if (ARROW_KEYS.includes(k)) { if (e.preventDefault) e.preventDefault(); return; }

    // Layer 0, reserved: F1 cycles the idle workers, the one macro key worth
    // stealing from SC2. No def hotkey may be a function key (there is a test).
    if (k === 'F1' && this.hud && typeof this.hud.selectNextIdleWorker === 'function') {
      if (e.preventDefault) e.preventDefault();
      this.hud.selectNextIdleWorker();
      return;
    }

    const mods = { shift: !!e.shiftKey, ctrl: !!e.ctrlKey, meta: !!e.metaKey, alt: !!e.altKey };

    // ONE resolver, ONE table, an explicit precedence order. Whatever comes
    // back was claimed by exactly one layer; null means no layer wanted it and
    // nothing runs. Nobody else is listening, so there is no second chance and
    // no double fire -- see the constructor.
    const hit = resolveKey(raw, mods, this.keyContext());
    this.lastKey = { key: raw, layer: hit ? hit.layer : null, action: hit ? hit.action : null };
    if (!hit || hit.layer === 'camera') return;
    if (e.preventDefault) e.preventDefault();

    // A greyed button still owns its key. Saying WHY is the whole difference
    // between "this game ignores my keyboard" and "Requires Supply Depot".
    //
    // One exception, and it matches Brood War: a structure you cannot yet
    // AFFORD still arms its placement rectangle. You walk the worker out to the
    // spot while the income arrives, and the click refuses if it is still short
    // (_confirmPlacement re-checks). Refusing to even show the ghost is what
    // made blind testers conclude the build menu was keyboard-dead: they
    // pressed B,S on the opening 50 minerals and nothing whatsoever happened.
    // A missing TECH requirement is different and still refuses outright.
    if (hit.button && hit.button.enabled === false) {
      const a = hit.action;
      const affordableLater = a && a.type === 'build' && techMet(this.world, this.player, a.defId);
      if (!affordableLater) {
        const reason = hit.button.reason || 'Unavailable';
        // Production accounts for itself the way the build menu does: not just
        // a flash that fades, but a line that stands until it stops being true.
        if (a && (a.type === 'train' || a.type === 'research')) {
          this._refuseProduction(a.defId, reason, hit.button.label);
        } else {
          this._flash(reason);
        }
        return;
      }
      this._flash(hit.button.reason || '');
    }
    this.runAction(hit.action, mods);
  }

  _onKeyUp(e) {
    const raw = e.key;
    if (raw === undefined || raw === null) return;
    this.keys.delete(raw.length === 1 ? raw.toLowerCase() : raw);
  }

  /**
   * Execute an action. This is the ONE executor: the keyboard, the command
   * card's buttons and the HUD's own routing all end up here, so a button and
   * its key are the same behaviour rather than two implementations of one idea.
   * Card verbs ('attack', 'build', 'cancelmenu', ...) are translated into this
   * layer's vocabulary by toInputAction() first.
   */
  runAction(raw, mods = {}) {
    const action = toInputAction(raw);
    if (!action) return;
    const sel = this.selectedEntities();
    const unitIds = sel.filter((e) => e.kind === 'unit').map((e) => e.id);

    switch (action.type) {
      case 'escape':
        if (this.mode !== 'normal' || this.cursor || this.pending) this.cancelMode();
        break;
      case 'group':
        if (this.mode === 'build') this.cancelMode();
        this._group(action.slot, action.mode);
        break;
      case 'cursor':
        if (unitIds.length || action.cursor === 'rally') {
          this.cursor = action.cursor;
          this.mode = 'normal';
          this.placementDef = null;
          this.pending = null;
          this.pendingAction = null;
        }
        break;
      case 'stop':
        if (unitIds.length) this.issue({ type: 'stop', ids: unitIds });
        this.cancelMode();
        break;
      case 'hold':
        if (unitIds.length) this.issue({ type: 'hold', ids: unitIds, queued: !!mods.shift });
        this.cancelMode();
        break;
      case 'buildmenu':
        if (this._selectedBuilder()) { this.mode = 'build'; this.cursor = null; this.pending = null; }
        break;
      case 'placement':
        this.beginPlacement(action.defId);
        break;
      case 'train':
        this.issueTrain(action.defId);
        break;
      case 'research':
        this.issueResearch(action.defId);
        break;
      case 'siege': {
        const tanks = sel.filter((e) => UnitDefs[e.defId] && UnitDefs[e.defId].canSiege);
        if (!tanks.length) break;
        const want = tanks.some((t) => !t.sieged) ? 'siege' : 'unsiege';
        this.issue({ type: want, ids: tanks.map((t) => t.id) });
        break;
      }
      case 'cloak': {
        const wraiths = sel.filter((e) => UnitDefs[e.defId] && UnitDefs[e.defId].canCloak);
        if (!wraiths.length) break;
        const want = wraiths.some((t) => !t.cloaked) ? 'cloak' : 'decloak';
        this.issue({ type: want, ids: wraiths.map((t) => t.id) });
        break;
      }
      // The card's Cancel button, on a building that is building something.
      case 'cancelproduction':
        this.cancelProduction(action.slot);
        break;
      default: break;
    }
  }

  /** Queue a unit at the selected building(s), or at any free trainer. */
  issueTrain(defId) {
    if (!UnitDefs[defId]) return false;
    const reason = unavailableReason(this.world, this.player, defId);
    if (reason) return this._refuseProduction(defId, reason);
    const ids = this.selectedEntities().filter((e) => e.kind === 'building').map((e) => e.id);
    this.issue({ type: 'train', ids, defId });
    if (this.productionStatus && this.productionStatus.defId === defId) this.productionStatus = null;
    return true;
  }

  issueResearch(defId) {
    if (!ResearchDefs[defId]) return false;
    const reason = unavailableReason(this.world, this.player, defId);
    if (reason) return this._refuseProduction(defId, reason);
    const ids = this.selectedEntities().filter((e) => e.kind === 'building').map((e) => e.id);
    if (!ids.length) return false;
    this.issue({ type: 'research', ids, defId });
    if (this.productionStatus && this.productionStatus.defId === defId) this.productionStatus = null;
    return true;
  }

  cancelProduction(slot) {
    const ids = this.selectedEntities().filter((e) => e.kind === 'building').map((e) => e.id);
    if (!ids.length) return false;
    this.issue({ type: 'cancel', ids, slot });
    return true;
  }

  // ---- control groups ------------------------------------------------------
  _group(slot, mode) {
    const get = (id) => this.world.get(id);
    const cur = pruneIds(this.controlGroups[slot] || [], get);
    this.controlGroups[slot] = cur;

    if (mode === 'assign' || mode === 'append') {
      if (!this.selectionOrder.length) return;
      this.controlGroups[slot] = applyGroup(this.controlGroups, slot, mode, this.selectionOrder, SELECTION_LIMIT);
      return;
    }

    if (!cur.length) { this._lastGroup = { slot, t: now() }; return; }
    this.setSelection(cur);
    const t = now();
    if (this._lastGroup.slot === slot && t - this._lastGroup.t <= GROUP_TAP_MS) {
      const c = centroid(this.selectedEntities());
      if (c && this.renderer.camera) this.renderer.camera.centerOn(c.x, c.y);
    }
    this._lastGroup = { slot, t };
  }

  // ---- minimap -------------------------------------------------------------
  _minimapPoint(e, clamped) {
    const mm = this.minimap;
    if (!mm) return null;
    const r = this._rectOf(this.minimapCanvas);
    const px = (e.clientX || 0) - (r.left || 0);
    const py = (e.clientY || 0) - (r.top || 0);
    if (clamped && typeof mm.clampedHitTest === 'function') return mm.clampedHitTest(px, py);
    return mm.hitTest(px, py);
  }

  _onMinimapDown(e) {
    const p = this._minimapPoint(e, false);
    if (e.preventDefault) e.preventDefault();
    if (!p) return;
    if (e.button === 2) {
      const target = this._worldPick(p.x, p.y);
      this._contextual(target, p.x, p.y, !!e.shiftKey);
      return;
    }
    if (e.button !== 0) return;
    if (this.mode === 'placement' || this.cursor) {
      // A minimap left click while targeting is a jump, not a command point.
      this.cancelMode();
    }
    this.minimapDrag = true;
    if (this.renderer.camera) this.renderer.camera.centerOn(p.x, p.y);
  }

  _minimapTo(e) {
    const p = this._minimapPoint(e, true);
    if (p && this.renderer.camera) this.renderer.camera.centerOn(p.x, p.y);
  }

  /** Nearest visible ENEMY thing to a world point -- minimap attack targeting. */
  _worldPick(x, y, reach = 2.0) {
    let best = null, bd = reach;
    for (const id of this.world.byPlayer[1 - this.player]) {
      const e = this.world.get(id);
      if (!e || !e.alive) continue;
      if (!canSee(this.world, this.player, e)) continue;
      const d = edgeDist(e, x, y);
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }

  // ---- per-frame -----------------------------------------------------------
  update(dt) {
    const get = (id) => this.world.get(id);

    // dead units fall out of the selection and out of every control group
    const pruned = pruneIds(this.selectionOrder, get);
    if (pruned.length !== this.selectionOrder.length) { this.selectionOrder = pruned; this._syncSelection(); }
    for (let i = 0; i < this.controlGroups.length; i++) {
      const g = this.controlGroups[i];
      if (!g.length) continue;
      const p = pruneIds(g, get);
      if (p.length !== g.length) this.controlGroups[i] = p;
    }

    if (this._selDirty) {
      if (this.renderer.setSelection) this.renderer.setSelection(this.selectionOrder);
      this._selDirty = false;
    }

    // The build submenu belongs to a worker. Losing the worker (it died, or the
    // player clicked something else) closes it, so `mode` can never describe a
    // page the card is not showing -- which is the state the keyboard resolves
    // against, and therefore has to be true.
    if (this.mode !== 'normal' && !this._selectedBuilder()) this.cancelMode();

    // building ghost follows the cursor while placing
    if (this.mode === 'placement' && this.placementDef) {
      const w = this.worldAt(this.mouse.x, this.mouse.y);
      const spot = validatePlacement(this.world, this.player, this.placementDef, w.x, w.y);
      const cost = unavailableReason(this.world, this.player, this.placementDef);
      // The ghost paints per-tile red/green, which is unreadable to anything
      // that cannot see the screen -- including every automated critic. The
      // simulation already produced the sentence; publish it.
      this.placementStatus = {
        defId: this.placementDef, x: w.x, y: w.y,
        ok: spot.ok && !cost,
        reason: spot.ok ? cost : spot.reason,
      };
      if (this.renderer.setPlacement) {
        this.renderer.setPlacement(this.placementDef, w.x, w.y, this.placementStatus.ok);
        this._ghostOn = true;
      }
    } else {
      this.placementStatus = null;
      if (this._ghostOn && this.renderer.setPlacement) {
        this.renderer.setPlacement(null);
        this._ghostOn = false;
      }
    }

    this._updateProduction();

    // camera: arrow keys plus pointer edge scroll
    this._camKeys.clear();
    for (const k of ARROW_KEYS) if (this.keys.has(k)) this._camKeys.add(k);
    if (this.renderer.camera && typeof this.renderer.camera.update === 'function') {
      this.renderer.camera.update(dt, this._camKeys, this._edge());
    }

    this._paintBox();
    this._paintCursor();
  }

  _edge() {
    if (!this.mouse.inside || this.minimapDrag) return null;
    if (this.hud && typeof this.hud.isOverHud === 'function'
        && this.hud.isOverHud(this.mouse.cx, this.mouse.cy)) return null;
    const { w, h } = this._viewport();
    const { x, y } = this.mouse;
    if (x < -1 || y < -1 || x > w + 1 || y > h + 1) return null;
    let ex = 0, ey = 0;
    if (x <= EDGE_PX) ex = -1; else if (x >= w - 1 - EDGE_PX) ex = 1;
    if (y <= EDGE_PX) ey = -1; else if (y >= h - 1 - EDGE_PX) ey = 1;
    return ex || ey ? { x: ex, y: ey } : null;
  }

  // ---- the selection rectangle (a plain DOM overlay, not a GL pass) --------
  _ensureBoxEl() {
    if (this._boxEl || typeof document === 'undefined') return this._boxEl;
    const host = (this.canvas && this.canvas.parentNode) || (document.body || null);
    if (!host || !document.createElement) return null;
    const el = document.createElement('div');
    el.className = 'sel-box';
    const s = el.style;
    s.position = 'fixed';
    s.border = '1px solid #7dff9b';
    s.background = 'rgba(125,255,155,0.10)';
    s.pointerEvents = 'none';
    s.display = 'none';
    s.zIndex = '20';
    host.appendChild(el);
    this._boxEl = el;
    return el;
  }

  _paintBox() {
    const el = this._ensureBoxEl();
    if (!el) return;
    const d = this.drag;
    if (!d || !d.moved) { el.style.display = 'none'; return; }
    const r = normRect(d.x0, d.y0, d.x1, d.y1);
    const rect = this._rectOf(this.canvas);
    const top = rect.top || 0;
    // Never draw over the command bar: a rectangle across the chrome reads as a
    // rendering bug, and the bar is not part of the battlefield anyway.
    let y1 = r.y1;
    if (this.hud && this.hud.barHeight) {
      const vh = (typeof window !== 'undefined' && window.innerHeight) || this._viewport().h;
      y1 = Math.min(y1, vh - this.hud.barHeight - top);
    }
    el.style.display = 'block';
    el.style.left = `${(rect.left || 0) + r.x0}px`;
    el.style.top = `${top + r.y0}px`;
    el.style.width = `${Math.max(0, r.x1 - r.x0)}px`;
    el.style.height = `${Math.max(0, y1 - r.y0)}px`;
  }

  _paintCursor() {
    const cv = this.canvas;
    if (!cv || !cv.style) return;
    const want = this.mode === 'placement' ? 'copy'
      : this.cursor === 'attackmove' ? 'crosshair'
      : this.cursor ? 'crosshair'
      : this.mode === 'build' ? 'help'
      : 'default';
    if (cv.style.cursor !== want) cv.style.cursor = want;
  }
}

export default InputController;
